<script setup lang="ts">
  import { storeToRefs } from 'pinia'
  import { useConfigStore } from '@/stores/ConfigStore'
  import colorsea from 'colorsea'
  import { watch, onMounted, onUnmounted } from 'vue'
  import { useAuthStore } from '@/stores/AuthStore'
  import { IonApp } from '@ionic/vue'
  import { useNativeStyle } from './composables/useNativeStyle'
  import router from './router'
  import { watchImmediate } from '@vueuse/core'
  import { getRouteBarBackgroundStyles } from './router/routes/utils'

  const { custom_color, custom_design_enabled } = storeToRefs(useConfigStore())
  const authStore = useAuthStore()
  const route = router.currentRoute

  const { adaptNativeStyle } = useNativeStyle()

  // Function to convert hex color to RGB values
  function hexToRgb(hex: string): string {
    // Remove the hash if present
    const cleanHex = hex.replace('#', '')

    // Parse the hex values
    const r = parseInt(cleanHex.substring(0, 2), 16)
    const g = parseInt(cleanHex.substring(2, 4), 16)
    const b = parseInt(cleanHex.substring(4, 6), 16)

    return `${r}, ${g}, ${b}`
  }

  // Create and manage style element
  let styleElement: HTMLStyleElement | null = null

  function createStyleElement() {
    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = 'app-color-configuration'
      document.head.appendChild(styleElement)
    }
  }

  function updateStyleElement(colors: {
    primaryColor: string
    primaryLightColor: string
    primaryRgb: string
    primaryLightRgb: string
  }) {
    if (!styleElement) createStyleElement()

    const cssContent = `
      :root,
      html,
      body,
      ion-app,
      ion-modal {
        --primary: ${colors.primaryColor};
        --primary-light: ${colors.primaryLightColor};
        --primary-rgb: ${colors.primaryRgb};
        --primary-light-rgb: ${colors.primaryLightRgb};
      }
    `

    styleElement!.textContent = cssContent
  }

  function removeStyleElement() {
    if (styleElement) {
      document.head.removeChild(styleElement)
      styleElement = null
    }
  }

  watchImmediate(route, () => {
    void adaptNativeStyle(getRouteBarBackgroundStyles(route.value))
  })

  const clockinPrimaryColor = '#1a9dd9'
  const clockinPrimaryColorLight = '#5EBAE4'

  watchImmediate([custom_design_enabled, custom_color], () => {
    const hasCustomColor = !!custom_color?.value
    const useCustomColor = custom_design_enabled?.value && hasCustomColor
    const primaryColor = useCustomColor
      ? custom_color.value!
      : clockinPrimaryColor
    const primaryLightColor = useCustomColor
      ? colorsea(custom_color.value!).lighten(20).hex()
      : clockinPrimaryColorLight

    // Convert colors to RGB format
    const primaryRgb = hexToRgb(primaryColor)
    const primaryLightRgb = hexToRgb(primaryLightColor)

    // Update style element with new colors
    updateStyleElement({
      primaryColor,
      primaryLightColor,
      primaryRgb,
      primaryLightRgb,
    })
  })

  watch(authStore, () => {
    if (!authStore.deviceIsAuthenticated) {
      useConfigStore().custom_design_enabled = false
    }
  })

  onMounted(() => {
    createStyleElement()
  })

  onUnmounted(() => {
    removeStyleElement()
  })
</script>
<template>
  <ion-app>
    <slot></slot>
  </ion-app>
</template>
